import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { Slot, WorkingHours } from './types'

dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(utc)
dayjs.extend(timezone)

export type UnavailabilitySlot = {
  startDate: string
  endDate: string
  startTime?: number | null
  endTime?: number | null
}

function isSlotBusy(slot: Slot, busySlots: Slot[]): boolean {
  return busySlots.some((busySlot) => {
    return dayjs(slot.start).isSame(dayjs(busySlot.start)) || dayjs(busySlot.end).isBetween(dayjs(slot.start), dayjs(slot.end))
  })
}

function isSlotUnavailable(slotStart: dayjs.Dayjs, slotEnd: dayjs.Dayjs, day: dayjs.Dayjs, unavailabilities?: UnavailabilitySlot[]): boolean {
  if (!unavailabilities || unavailabilities.length === 0) return false

  return unavailabilities.some((u) => {
    const uStart = dayjs(u.startDate).startOf('day')
    const uEnd = dayjs(u.endDate).startOf('day')

    // Check if the day falls within the unavailability date range
    if (!day.isSameOrAfter(uStart, 'day') || !day.isSameOrBefore(uEnd, 'day')) return false

    // Full day block
    if (u.startTime == null && u.endTime == null) return true

    // Partial day block — check time overlap
    const slotStartMinutes = slotStart.hour() * 60 + slotStart.minute()
    const slotEndMinutes = slotEnd.hour() * 60 + slotEnd.minute()
    const blockStart = u.startTime ?? 0
    const blockEnd = u.endTime ?? 1440

    return slotStartMinutes < blockEnd && slotEndMinutes > blockStart
  })
}

export function generateSlots(
  day: Date,
  workingHours: WorkingHours[],
  busySlots: Slot[],
  duration: number,
  timeZone?: string,
  unavailabilities?: UnavailabilitySlot[]
): Slot[] {
  if (duration <= 0) return []

  const dayObj = dayjs(day)

  return workingHours.reduce((slots: Slot[], workingHour) => {
    const dayOfWeek = dayjs(day).day()
    if (workingHour.days.includes(dayOfWeek)) {
      const numberOfSlots = Math.floor((workingHour.endTime - workingHour.startTime) / duration)
      for (let i = 0; i < numberOfSlots; i++) {
        const start = dayjs(day)
          .tz(timeZone || dayjs.tz.guess())
          .startOf('day')
          .add(workingHour.startTime + i * duration, 'minute')
        const end = start.clone().add(duration, 'minute')
        const slot = {
          start: start.toISOString(),
          end: end.toISOString(),
        }
        if (!isSlotBusy(slot, busySlots) && dayjs(slot.start).isSameOrAfter(dayjs()) && !isSlotUnavailable(start, end, dayObj, unavailabilities)) {
          slots.push(slot)
        }
      }
    }
    return slots
  }, [])
}
